// KYC / Identity verification for payout — required before withdrawing
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Shield, Check, X, Hourglass } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Verify() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    legal_name: "", country: "New Zealand", id_number: "", id_document_url: "",
    id_type: "drivers_licence", tax_file_number: "",
    payout_method: "paypal", paypal_email: "",
    bank_account_name: "", bank_account_number: "", bank_routing: "",
  });
  const [busy, setBusy] = useState(false);
  const [idUploadBusy, setIdUploadBusy] = useState(false);

  const uploadIdDocument = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 15_000_000) { toast.error("Max 15MB"); return; }
    setIdUploadBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("private", "true");
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      setForm((cur) => ({ ...cur, id_document_url: url }));
      toast.success("ID photo uploaded");
    } catch { toast.error("Upload failed"); }
    setIdUploadBusy(false);
  };

  useEffect(() => {
    if (!user) return;
    api.get("/kyc/status").then(({ data }) => setStatus(data));
  }, [user]);

  if (!user) return <div className="p-20 text-center text-white/40">Sign in to verify your account.</div>;

  const submit = async () => {
    if (!form.legal_name || !form.id_number) { toast.error("Legal name and ID number required"); return; }
    if (form.payout_method === "paypal" && !form.paypal_email) { toast.error("PayPal email required"); return; }
    if (form.payout_method === "bank" && (!form.bank_account_name || !form.bank_account_number)) { toast.error("Bank details required"); return; }
    setBusy(true);
    try {
      await api.post("/kyc/submit", form);
      toast.success("Submitted · pending owner review");
      const { data } = await api.get("/kyc/status"); setStatus(data);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    setBusy(false);
  };

  const s = status?.status || "not_submitted";

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="verify-page">
      <Toaster theme="dark" position="top-center" />
      <div>
        <div className="label-eyebrow mb-2">// payout verification</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Verify to get paid.</h1>
        <p className="text-white/50 mt-3">We verify your identity & payout method once. After approval you can convert NX → real money via PayPal or bank transfer.</p>
      </div>

      <div className="flex items-center gap-4">
        <div className={`px-4 py-2 border ${s === "approved" ? "border-nexus-mint text-nexus-mint" : s === "pending" ? "border-nexus-amber text-nexus-amber" : s === "rejected" ? "border-nexus-crimson text-nexus-crimson" : "border-white/20 text-white/50"} text-[10px] tracking-[0.3em] uppercase font-display flex items-center gap-2`} data-testid="kyc-status">
          {s === "approved" ? <Check className="w-3 h-3" /> : s === "pending" ? <Hourglass className="w-3 h-3" /> : s === "rejected" ? <X className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
          {s.replace("_", " ")}
        </div>
        {s === "approved" && <button onClick={() => nav("/wallet")} className="btn-nexus !py-2 !px-4 text-[10px]">Withdraw now</button>}
      </div>

      {(s === "not_submitted" || s === "rejected") && (
        <div className="glass p-6 space-y-4" data-testid="verify-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="label-eyebrow mb-2">legal name</div>
              <input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="kyc-name" />
            </div>
            <div>
              <div className="label-eyebrow mb-2">country</div>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">id type</div>
            <div className="flex flex-wrap gap-2">
              {[["drivers_licence","Driver's licence"],["passport","Passport"],["national_id","National ID"]].map(([v,l]) => (
                <button key={v} onClick={() => setForm({ ...form, id_type: v })} className="text-[10px] tracking-[0.2em] uppercase font-display px-3 py-2 border" style={{ borderColor: form.id_type === v ? "#00F0FF" : "rgba(255,255,255,0.1)", color: form.id_type === v ? "#00F0FF" : "rgba(255,255,255,0.5)" }} data-testid={`kyc-id-type-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">government id number</div>
            <input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="kyc-id" />
            <div className="text-[10px] text-white/40 mt-1 tracking-wider">Only the last 4 digits are stored. Full value is destroyed after verification.</div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">photo id upload (required for withdrawal approval)</div>
            {form.id_document_url ? (
              <div className="flex items-center gap-3">
                <img src={form.id_document_url} alt="ID document" className="h-16 border border-white/10 object-cover" />
                <label className="btn-ghost !py-1.5 !px-3 text-[10px] cursor-pointer">
                  {idUploadBusy ? "Uploading…" : "Replace"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadIdDocument} disabled={idUploadBusy} />
                </label>
              </div>
            ) : (
              <label className="btn-ghost w-full justify-center cursor-pointer" data-testid="kyc-id-upload">
                {idUploadBusy ? "Uploading…" : "Upload a photo of your ID"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadIdDocument} disabled={idUploadBusy} />
              </label>
            )}
            <div className="text-[10px] text-white/40 mt-1 tracking-wider">Owner-only visibility. Used solely to confirm your identity before payouts.</div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">tax file number (optional — required in some countries before payout)</div>
            <input value={form.tax_file_number} onChange={(e) => setForm({ ...form, tax_file_number: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono" data-testid="kyc-tfn" />
            <div className="text-[10px] text-white/40 mt-1 tracking-wider">Only the last 4 digits are stored, same as your ID number. Used to generate tax invoices/receipts for your purchases, subscriptions and payouts.</div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">payout method</div>
            <div className="flex gap-2">
              {["paypal", "bank"].map((m) => (
                <button key={m} onClick={() => setForm({ ...form, payout_method: m })} className="text-[10px] tracking-[0.3em] uppercase font-display px-4 py-2 border" style={{ borderColor: form.payout_method === m ? "#00F0FF" : "rgba(255,255,255,0.1)", color: form.payout_method === m ? "#00F0FF" : "rgba(255,255,255,0.5)" }}>{m}</button>
              ))}
            </div>
          </div>
          {form.payout_method === "paypal" ? (
            <div>
              <div className="label-eyebrow mb-2">paypal email</div>
              <input value={form.paypal_email} onChange={(e) => setForm({ ...form, paypal_email: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="kyc-paypal" />
            </div>
          ) : (
            <div className="space-y-3">
              <div><div className="label-eyebrow mb-2">account name</div><input value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><div className="label-eyebrow mb-2">account #</div><input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono" /></div>
                <div><div className="label-eyebrow mb-2">routing / SWIFT</div><input value={form.bank_routing} onChange={(e) => setForm({ ...form, bank_routing: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono" /></div>
              </div>
            </div>
          )}
          <button onClick={submit} disabled={busy} className="btn-nexus w-full justify-center" data-testid="kyc-submit">{busy ? "Submitting…" : "Submit for review"}</button>
        </div>
      )}

      {s === "pending" && (
        <div className="glass p-6 text-center space-y-3">
          <Hourglass className="w-10 h-10 mx-auto text-nexus-amber" />
          <div className="font-display tracking-wide">Your details are with the team.</div>
          <div className="text-sm text-white/50">Approval is usually within 24 hours. You'll be notified when complete.</div>
        </div>
      )}

      {s === "approved" && status && (
        <div className="glass p-6 space-y-2">
          <div className="font-display tracking-wide text-nexus-mint">Verified · ready to withdraw.</div>
          <div className="text-xs text-white/50">Payout method: {status.payout_method} · {status.paypal_email || `bank ending ${status.bank_account_last4}`}</div>
        </div>
      )}
      <TaxInvoiceHistory />
    </div>
  );
}

function TaxInvoiceHistory() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/me/tax-invoices").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="glass p-6 space-y-3" data-testid="tax-invoice-history">
      <div className="label-eyebrow">// tax invoices &amp; receipts</div>
      <p className="text-xs text-white/50">Every coin bundle, subscription, ad campaign payment and withdrawal automatically gets a receipt emailed to you and listed here — keep these for your own tax records.</p>
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
            <div>
              <div>{i.label}</div>
              <div className="text-[10px] text-white/40">{i.invoice_no} · {(i.issued_at || "").slice(0, 10)}</div>
            </div>
            <div className="font-mono text-xs">{i.amount < 0 ? "-" : ""}{Math.abs(i.amount).toFixed(2)} {i.currency}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
